import React from 'react';
import { Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { RECONOCIMIENTO_OPTIONS } from './courseConstants';
import SignaturePad from '../../components/SignaturePad';

const CourseFormDialog = ({
  open,
  onOpenChange,
  editingCourse,
  formData,
  setFormData,
  onSubmit,
  onTemplateSelect,
  handleNumeroSesionesChange,
  updateSesion,
  toggleReconocimiento,
  families,
  templates,
  directors,
  instructors,
  onlineCourses
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="course-dialog-title">
            {editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="course-form">
          {/* Plantilla */}
          {!editingCourse && templates.length > 0 && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <Label htmlFor="template">Usar Curso Predefinido (Opcional)</Label>
              <Select value={formData.template_id} onValueChange={onTemplateSelect}>
                <SelectTrigger data-testid="course-form-template">
                  <SelectValue placeholder="Seleccionar curso predefinido..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Crear desde cero</SelectItem>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                Selecciona un curso predefinido para cargar los datos automáticamente
              </p>
            </div>
          )}
          
          {/* Familia y Subfamilia */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="familia">Familia</Label>
              <Select value={formData.familia} onValueChange={(value) => setFormData({ ...formData, familia: value, subfamilia: '' })}>
                <SelectTrigger data-testid="course-form-familia">
                  <SelectValue placeholder="Seleccionar familia..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(families).map(([codigo, data]) => (
                    <SelectItem key={codigo} value={codigo}>
                      {data.nombre} ({codigo})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subfamilia">Subfamilia</Label>
              <Select 
                value={formData.subfamilia} 
                onValueChange={(value) => setFormData({ ...formData, subfamilia: value })}
                disabled={!formData.familia}
              >
                <SelectTrigger data-testid="course-form-subfamilia">
                  <SelectValue placeholder="Seleccionar subfamilia..." />
                </SelectTrigger>
                <SelectContent>
                  {formData.familia && families[formData.familia]?.subfamilias && 
                    Object.entries(families[formData.familia].subfamilias).map(([codigo, nombre]) => (
                      <SelectItem key={codigo} value={codigo}>
                        {nombre} ({codigo})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Nombre y Descripción */}
          <div>
            <Label htmlFor="nombre">Nombre del Curso *</Label>
            <Input
              id="nombre"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
              data-testid="course-form-nombre"
            />
          </div>
          <div>
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={3}
              data-testid="course-form-descripcion"
            />
          </div>

          {/* Horas y Sesiones */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="horas">Horas *</Label>
              <Input
                id="horas"
                type="number"
                value={formData.horas}
                onChange={(e) => setFormData({ ...formData, horas: e.target.value })}
                required
                data-testid="course-form-horas"
              />
            </div>
            <div>
              <Label htmlFor="numero_sesiones">Número de Sesiones</Label>
              <Input
                id="numero_sesiones"
                type="number"
                min="1"
                max="30"
                value={formData.numero_sesiones}
                onChange={(e) => handleNumeroSesionesChange(e.target.value)}
                data-testid="course-form-numero-sesiones"
                placeholder="Indica el nº de sesiones"
              />
            </div>
          </div>

          {/* Sesiones dinámicas */}
          {formData.sesiones.length > 0 && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Calendar className="w-4 h-4" />
                <Label className="font-medium">Programación de Sesiones</Label>
              </div>
              {formData.sesiones.map((sesion, index) => (
                <div key={index} className="bg-white p-3 rounded-md border border-slate-200">
                  <p className="text-sm font-medium text-slate-600 mb-2">Sesión {index + 1}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Fecha Inicio</Label>
                      <Input
                        type="date"
                        value={sesion.fecha_inicio}
                        onChange={(e) => updateSesion(index, 'fecha_inicio', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fecha Fin</Label>
                      <Input
                        type="date"
                        value={sesion.fecha_fin}
                        onChange={(e) => updateSesion(index, 'fecha_fin', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Hora Inicio</Label>
                      <Input
                        type="time"
                        value={sesion.hora_inicio}
                        onChange={(e) => updateSesion(index, 'hora_inicio', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Hora Fin</Label>
                      <Input
                        type="time"
                        value={sesion.hora_fin}
                        onChange={(e) => updateSesion(index, 'hora_fin', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Instructores */}
          <div>
            <Label htmlFor="instructores">Instructores</Label>
            <div className="border rounded-md p-3 space-y-2 bg-slate-50">
              {instructors.length === 0 ? (
                <p className="text-xs text-amber-600">
                  No hay instructores dados de alta. Debe crear usuarios con rol Instructor.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-2">Selecciona uno o más instructores:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {instructors.map(instructor => (
                      <div key={instructor.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`instructor-${instructor.id}`}
                          checked={formData.instructor_ids.includes(instructor.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, instructor_ids: [...formData.instructor_ids, instructor.id] });
                            } else {
                              setFormData({ ...formData, instructor_ids: formData.instructor_ids.filter(id => id !== instructor.id) });
                            }
                          }}
                        />
                        <label 
                          htmlFor={`instructor-${instructor.id}`}
                          className="text-sm cursor-pointer"
                        >
                          {instructor.nombre} {instructor.apellidos}
                        </label>
                      </div>
                    ))}
                  </div>
                  {formData.instructor_ids.length > 0 && (
                    <p className="text-xs text-slate-600 mt-2">
                      {formData.instructor_ids.length} instructor(es) seleccionado(s)
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Director */}
          <div>
            <Label htmlFor="director_formacion">Director de Formación</Label>
            <Select 
              value={formData.director_formacion_id || undefined} 
              onValueChange={(value) => setFormData({ ...formData, director_formacion_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger data-testid="course-form-director">
                <SelectValue placeholder="Seleccionar director..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {directors.map(director => (
                  <SelectItem key={director.id} value={director.id}>
                    {director.nombre} {director.apellidos}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cliente */}
          <div>
            <Label htmlFor="cliente">Cliente</Label>
            <Input
              id="cliente"
              value={formData.cliente}
              onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
              data-testid="course-form-cliente"
            />
          </div>

          {/* Importe de la Formación */}
          <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
            <Label className="text-emerald-800 font-medium mb-3 block">
              💰 Importe de la Formación
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coste_total" className="text-xs text-emerald-700">
                  Coste Total de la Formación (€)
                </Label>
                <Input
                  id="coste_total"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.coste_total}
                  onChange={(e) => setFormData({ ...formData, coste_total: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="course-form-coste-total"
                />
              </div>
              <div>
                <Label htmlFor="coste_por_alumno" className="text-xs text-emerald-700">
                  Coste por Alumno (€)
                </Label>
                <Input
                  id="coste_por_alumno"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.coste_por_alumno}
                  onChange={(e) => setFormData({ ...formData, coste_por_alumno: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="course-form-coste-alumno"
                />
              </div>
            </div>
            <p className="text-xs text-emerald-600 mt-2">
              Puede indicar el coste total, el coste por alumno, o ambos.
            </p>
          </div>

          {/* Lugar de Impartición */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lugar_imparticion">Lugar de Impartición</Label>
              <Input
                id="lugar_imparticion"
                value={formData.lugar_imparticion}
                onChange={(e) => setFormData({ ...formData, lugar_imparticion: e.target.value })}
                data-testid="course-form-lugar-imparticion"
              />
            </div>
            <div>
              <Label htmlFor="localidad">Localidad</Label>
              <Input
                id="localidad"
                value={formData.localidad}
                onChange={(e) => setFormData({ ...formData, localidad: e.target.value })}
                data-testid="course-form-localidad"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="provincia">Provincia</Label>
            <Input
              id="provincia"
              value={formData.provincia}
              onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
              data-testid="course-form-provincia"
            />
          </div>

          {/* Enlace de Videoconferencia */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <Label htmlFor="meet_link" className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Enlace de Videoconferencia (Google Meet, Zoom, etc.)
            </Label>
            <Input
              id="meet_link"
              type="url"
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
              value={formData.meet_link || ''}
              onChange={(e) => setFormData({ ...formData, meet_link: e.target.value })}
              className="mt-2"
              data-testid="course-form-meet-link"
            />
            <p className="text-xs text-blue-600 mt-1">
              Añade el enlace de la reunión para que los alumnos puedan acceder a las sesiones online
            </p>
          </div>

          {/* Objetivos */}
          <div>
            <Label htmlFor="objetivos_generales">Objetivos Generales</Label>
            <Textarea
              id="objetivos_generales"
              value={formData.objetivos_generales}
              onChange={(e) => setFormData({ ...formData, objetivos_generales: e.target.value })}
              rows={3}
              data-testid="course-form-objetivos-generales"
            />
          </div>
          <div>
            <Label htmlFor="objetivos_especificos">Objetivos Específicos</Label>
            <Textarea
              id="objetivos_especificos"
              value={formData.objetivos_especificos}
              onChange={(e) => setFormData({ ...formData, objetivos_especificos: e.target.value })}
              rows={3}
              data-testid="course-form-objetivos-especificos"
            />
          </div>
          <div>
            <Label htmlFor="temario">Temario</Label>
            <Textarea
              id="temario"
              value={formData.temario}
              onChange={(e) => setFormData({ ...formData, temario: e.target.value })}
              rows={4}
              data-testid="course-form-temario"
            />
          </div>

          {/* Reconocimiento / Acreditación */}
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <Label className="text-amber-800 font-medium mb-3 block">
              🏅 Reconocimiento / Acreditación
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {RECONOCIMIENTO_OPTIONS.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`reconocimiento-${option.id}`}
                    checked={(formData.reconocimiento || []).includes(option.id)}
                    onCheckedChange={() => toggleReconocimiento(option.id)}
                  />
                  <label
                    htmlFor={`reconocimiento-${option.id}`}
                    className="text-sm text-slate-700 cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            
            {/* Campos condicionales de acreditación */}
            {(formData.reconocimiento || []).includes('cfc') && (
              <div className="mt-3 p-3 bg-amber-100 rounded-md">
                <Label htmlFor="acreditacion_cfc" className="text-xs text-amber-800 font-medium">
                  Número de Acreditación CFC:
                </Label>
                <Input
                  id="acreditacion_cfc"
                  value={formData.acreditacion_cfc}
                  onChange={(e) => setFormData({ ...formData, acreditacion_cfc: e.target.value })}
                  placeholder="Ej: CFC-2025-001234"
                  className="mt-1"
                />
              </div>
            )}
            
            {(formData.reconocimiento || []).includes('erc') && (
              <div className="mt-3 p-3 bg-amber-100 rounded-md">
                <Label htmlFor="acreditacion_erc" className="text-xs text-amber-800 font-medium">
                  Número de Acreditación ERC:
                </Label>
                <Input
                  id="acreditacion_erc"
                  value={formData.acreditacion_erc}
                  onChange={(e) => setFormData({ ...formData, acreditacion_erc: e.target.value })}
                  placeholder="Ej: ERC-2025-001234"
                  className="mt-1"
                />
              </div>
            )}
            
            {(formData.reconocimiento || []).includes('semes') && (
              <div className="mt-3 p-3 bg-amber-100 rounded-md">
                <Label htmlFor="acreditacion_semes" className="text-xs text-amber-800 font-medium">
                  Número de Acreditación SEMES:
                </Label>
                <Input
                  id="acreditacion_semes"
                  value={formData.acreditacion_semes}
                  onChange={(e) => setFormData({ ...formData, acreditacion_semes: e.target.value })}
                  placeholder="Ej: SEMES-2025-001234"
                  className="mt-1"
                />
              </div>
            )}
            
            {(formData.reconocimiento || []).includes('semicyuc') && (
              <div className="mt-3 p-3 bg-amber-100 rounded-md">
                <Label htmlFor="acreditacion_semicyuc" className="text-xs text-amber-800 font-medium">
                  Número de Acreditación SEMICYUC:
                </Label>
                <Input
                  id="acreditacion_semicyuc"
                  value={formData.acreditacion_semicyuc}
                  onChange={(e) => setFormData({ ...formData, acreditacion_semicyuc: e.target.value })}
                  placeholder="Ej: SEMICYUC-2025-001234"
                  className="mt-1"
                />
              </div>
            )}
            
            {(formData.reconocimiento || []).includes('otros') && (
              <div className="mt-3 p-3 bg-amber-100 rounded-md">
                <Label htmlFor="reconocimiento_otros" className="text-xs text-amber-800 font-medium">
                  Especificar entidad acreditadora:
                </Label>
                <Input
                  id="reconocimiento_otros"
                  value={formData.reconocimiento_otros}
                  onChange={(e) => setFormData({ ...formData, reconocimiento_otros: e.target.value })}
                  placeholder="Nombre de la entidad acreditadora..."
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Configuración de Certificados */}
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <Label className="text-amber-800 font-medium mb-3 block">
              Configuración de Certificados
            </Label>
            
            {/* Tipo de certificado */}
            <div className="mb-4">
              <Label htmlFor="tipo_certificado" className="text-sm">Tipo de Certificado</Label>
              <Select 
                value={formData.tipo_certificado || "propio"} 
                onValueChange={(value) => setFormData({ ...formData, tipo_certificado: value })}
              >
                <SelectTrigger data-testid="course-form-certificate-type">
                  <SelectValue placeholder="Seleccionar tipo de certificado..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="propio">Certificado Propio</SelectItem>
                  <SelectItem value="desa_cv">DESA Comunidad Valenciana</SelectItem>
                  <SelectItem value="cfc">Certificado CFC (Formación Continuada)</SelectItem>
                  <SelectItem value="manual">Certificado Manual (Texto personalizado)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Modalidad */}
            <div className="mb-4">
              <Label htmlFor="modalidad" className="text-sm">Modalidad de la Formación</Label>
              <Select 
                value={formData.modalidad || "presencial"} 
                onValueChange={(value) => setFormData({ ...formData, modalidad: value })}
              >
                <SelectTrigger data-testid="course-form-modalidad">
                  <SelectValue placeholder="Seleccionar modalidad..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="mixta">Mixta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campos específicos para CFC */}
            {formData.tipo_certificado === 'cfc' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="creditos_cfc" className="text-sm">Nº de Créditos CFC</Label>
                  <Input
                    id="creditos_cfc"
                    type="number"
                    step="0.1"
                    value={formData.creditos_cfc || ''}
                    onChange={(e) => setFormData({ ...formData, creditos_cfc: parseFloat(e.target.value) || '' })}
                    placeholder="Ej: 2.5"
                    data-testid="course-form-cfc-credits"
                  />
                </div>
                <div>
                  <Label htmlFor="numero_acreditacion_cfc" className="text-sm">Nº de Acreditación</Label>
                  <Input
                    id="numero_acreditacion_cfc"
                    value={formData.numero_acreditacion_cfc || ''}
                    onChange={(e) => setFormData({ ...formData, numero_acreditacion_cfc: e.target.value })}
                    placeholder="Ej: 09-AFAP-01234.5/2024"
                    data-testid="course-form-cfc-registration"
                  />
                </div>
              </div>
            )}

            {/* Texto personalizado para certificado manual */}
            {formData.tipo_certificado === 'manual' && (
              <div className="mb-4">
                <Label htmlFor="texto_certificado_manual" className="text-sm">Texto Personalizado</Label>
                <Textarea
                  id="texto_certificado_manual"
                  value={formData.texto_certificado_manual || ''}
                  onChange={(e) => setFormData({ ...formData, texto_certificado_manual: e.target.value })}
                  placeholder="Introduce el texto que aparecerá en el certificado..."
                  rows={3}
                  data-testid="course-form-manual-text"
                />
              </div>
            )}

            {/* Firma del director */}
            <SignaturePad
              currentSignature={formData.firma_director_url}
              onSave={(url) => setFormData({ ...formData, firma_director_url: url })}
              onClear={() => setFormData({ ...formData, firma_director_url: '' })}
            />
          </div>

          {/* Blended Learning */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <Label htmlFor="online_course_id" className="text-blue-800 font-medium">
              Formación Mixta (Blended Learning)
            </Label>
            <p className="text-xs text-blue-600 mb-2">
              Vincular un curso online como requisito previo para este curso presencial
            </p>
            <Select 
              value={formData.online_course_id || "none"} 
              onValueChange={(value) => setFormData({ ...formData, online_course_id: value === "none" ? "" : value })}
            >
              <SelectTrigger data-testid="course-form-online-course">
                <SelectValue placeholder="Seleccionar curso online..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin requisito online</SelectItem>
                {onlineCourses.map(course => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="course-form-cancel">
              Cancelar
            </Button>
            <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]" data-testid="course-form-submit">
              {editingCourse ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CourseFormDialog;
